import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Session } from '../types';
import './Sidebar.css';

function formatDate(isoString: string) {
  const date = new Date(isoString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface SidebarProps {
  sessions: Record<string, Session>;
  activeSessionId: string | null;
  collapsed: boolean;
  onNewChat: () => void;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  onLogout: () => void;
  onToggleCollapse: () => void;
  email: string;
}

function PanelLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}

function ChatBubbleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

export default function Sidebar({ sessions, activeSessionId, collapsed, onNewChat, onSelectSession, onDeleteSession, onLogout, onToggleCollapse, email }: SidebarProps) {
  const { theme, toggleTheme } = useTheme();
  const sessionList = Object.values(sessions).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <>
      {!collapsed && <div className="sidebar-backdrop" onClick={onToggleCollapse} />}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {/* ChargeHub Logo */}
          <div className="logo">
            <div className="logo-icon-svg">
              <img
                src="https://chargehub.com/hubfs/B2C/General%20Homepage/Optimized%20HP%20Images/New%20images/ChargeHub%20Logo%20Plug%20Icon%20-%20Clearspace_5.webp"
                alt="ChargeHub"
                width={32}
                height={32}
              />
            </div>
            <span className="logo-text">
              <span className="logo-charge">Charge</span>
              <span className="logo-hub">Hub</span>
            </span>
            <button className="collapse-btn" onClick={onToggleCollapse} title="Hide sidebar">
              <PanelLeftIcon />
            </button>
          </div>

          <button className="new-chat-btn" onClick={onNewChat}>
            <span className="new-chat-icon">+</span>
            New Chat
          </button>
        </div>

        <div className="sidebar-sessions">
          <p className="sessions-label">Recent Chats</p>
          {sessionList.length === 0 ? (
            <p className="no-sessions">No chats yet</p>
          ) : (
            sessionList.map((session) => (
              <div
                key={session.id}
                className={`session-item ${session.id === activeSessionId ? 'active' : ''}`}
                onClick={() => onSelectSession(session.id)}
              >
                <span className="session-icon">
                  <ChatBubbleIcon />
                </span>
                <div className="session-info">
                  <span className="session-title">{session.title}</span>
                  <span className="session-date">{formatDate(session.createdAt)}</span>
                </div>
                <button
                  className="session-delete"
                  onClick={(e) => { e.stopPropagation(); onDeleteSession(session.id); }}
                  title="Delete chat"
                >
                  <TrashIcon />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer with user email and logout */}
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{email.charAt(0).toUpperCase()}</div>
            <span className="user-email" title={email}>{email}</span>
          </div>
          <div className="footer-actions">
            <button className="theme-toggle-btn" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button className="logout-btn" onClick={onLogout} title="Logout">
              ↩
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
